import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/CustomAuthContext';
import { useNavigate } from 'react-router-dom';
import { itemsAPI, pokemonAPI, ItemSaleWithDetails, PokemonSaleWithDetails } from '../../services/api';
import { calculateHiddenPower, formatHiddenPower } from '../../utils/hiddenPower';
import './ReviewSubmissions.css';

type CombinedSubmission = (ItemSaleWithDetails | PokemonSaleWithDetails) & {
  type: 'Pokemon' | 'Item';
  submissionId: number;
  name: string;
  submittedAt: string;
};

interface EditData {
  submissionId: number;
  type: 'Pokemon' | 'Item';
  name: string;
  price: number;
  nature?: string;
  ability?: string;
  level?: number;
  hp_iv?: number;
  atk_iv?: number;
  def_iv?: number;
  spatk_iv?: number;
  spdef_iv?: number;
  speed_iv?: number;
  shiny?: boolean;
  form?: string;
}

const ReviewSubmissions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState<CombinedSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  const [selectedSubmission, setSelectedSubmission] = useState<CombinedSubmission | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState<EditData | null>(null);

  // Filter states for "All" tab only
  const [filters, setFilters] = useState({
    status: 'all' as 'all' | 'pending' | 'approved' | 'rejected',
    pokemon: '',
    priceMin: '',
    priceMax: '',
    soldDateFrom: '',
    soldDateTo: '',
    submittedFrom: '',
    submittedTo: '',
    submittedBy: '',
    nature: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15); // 15 items per page for review submissions

  // Check if user has permission to review
  useEffect(() => {
    if (user && !['checker', 'admin'].includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load submissions
  useEffect(() => {
    fetchSubmissions();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeTab]);



  const fetchSubmissions = async () => {
    if (!user || !['checker', 'admin'].includes(user.role)) return;

    setLoading(true);
    setError(null);

    try {
      const statusFilter = undefined; // Load all submissions for both tabs

      // Fetch both item and Pokemon submissions - use high limit to get all records for admin review
      const [itemResponse, pokemonResponse] = await Promise.all([
        itemsAPI.getAll({ status: statusFilter, limit: 10000 }),
        pokemonAPI.getAll({ status: statusFilter, limit: 10000 })
      ]);

      // Handle new API response format
      const itemSubmissions = Array.isArray(itemResponse) ? itemResponse : itemResponse.data;
      const pokemonSubmissions = Array.isArray(pokemonResponse) ? pokemonResponse : pokemonResponse.data;

      // Transform and combine the data
      const combinedSubmissions: CombinedSubmission[] = [
        ...itemSubmissions.map((item): CombinedSubmission => ({
          ...item,
          type: 'Item' as const,
          submissionId: item.id,
          name: item.item_name,
          submittedAt: item.date_submitted
        })),
        ...pokemonSubmissions.map((pokemon): CombinedSubmission => ({
          ...pokemon,
          type: 'Pokemon' as const,
          submissionId: pokemon.sale_id,
          name: pokemon.pokemon_name,
          submittedAt: pokemon.date_submitted
        }))
      ];

      // Sort by submission date (newest first)
      combinedSubmissions.sort((a, b) =>
        new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      );

      setSubmissions(combinedSubmissions);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter submissions based on active tab and exclude user's own PENDING submissions to prevent conflicts of interest
  // Note: Users can see their own approved/rejected submissions in "All" tab, but not their pending ones
  const filteredSubmissions = activeTab === 'pending'
    ? submissions.filter(submission => {
        // Exclude own submissions from pending tab unless user is admin
        if (user && submission.submitted_by_username === user.username && user.role !== 'admin') {
          return false;
        }
        return submission.status === 'pending';
      })
    : submissions.filter(submission => {
        // For "All" tab: Only exclude own PENDING submissions, but show own approved/rejected ones
        if (user && submission.submitted_by_username === user.username && submission.status === 'pending' && user.role !== 'admin') {
          return false;
        }

        // Status filter for "All" tab
        if (filters.status !== 'all' && submission.status !== filters.status) {
          return false;
        }

        // Pokemon name filter
        if (filters.pokemon && !submission.name.toLowerCase().includes(filters.pokemon.toLowerCase())) {
          return false;
        }

        // Price range filter
        if (filters.priceMin && submission.price < parseFloat(filters.priceMin)) {
          return false;
        }
        if (filters.priceMax && submission.price > parseFloat(filters.priceMax)) {
          return false;
        }

        // Sold date filter (for approved items)
        if (filters.soldDateFrom || filters.soldDateTo) {
          const soldDate = new Date(submission.date_sold || '');
          if (filters.soldDateFrom && soldDate < new Date(filters.soldDateFrom)) {
            return false;
          }
          if (filters.soldDateTo && soldDate > new Date(filters.soldDateTo)) {
            return false;
          }
        }

        // Submitted date filter
        if (filters.submittedFrom || filters.submittedTo) {
          const submittedDate = new Date(submission.submittedAt);
          if (filters.submittedFrom && submittedDate < new Date(filters.submittedFrom)) {
            return false;
          }
          if (filters.submittedTo && submittedDate > new Date(filters.submittedTo)) {
            return false;
          }
        }

        // Submitted by filter
        if (filters.submittedBy && !submission.submitted_by_username.toLowerCase().includes(filters.submittedBy.toLowerCase())) {
          return false;
        }

        // Nature filter (Pokemon only)
        if (filters.nature && submission.type === 'Pokemon') {
          const pokemonSubmission = submission as PokemonSaleWithDetails;
          if (!pokemonSubmission.nature || !pokemonSubmission.nature.toLowerCase().includes(filters.nature.toLowerCase())) {
            return false;
          }
        }

        return true;
      });

  // Pagination calculations
  const totalItems = filteredSubmissions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  // Tab counts excluding user's own PENDING submissions only
  const reviewablePendingCount = submissions.filter(s => {
    // Exclude own pending submissions unless user is admin
    if (user && s.submitted_by_username === user.username && user.role !== 'admin') {
      return false;
    }
    return s.status === 'pending';
  }).length;

  const reviewableTotalCount = submissions.filter(s => {
    // Only exclude own PENDING submissions if not admin, but count own approved/rejected ones
    if (user && s.submitted_by_username === user.username && s.status === 'pending' && user.role !== 'admin') {
      return false;
    }
    return true;
  }).length;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filters]);

  const viewDetails = (submission: CombinedSubmission) => {
    setSelectedSubmission(submission);
  };

  const handleReviewSubmission = async (submission: CombinedSubmission, action: 'approve' | 'reject') => {
    setActionLoading(true);

    try {
      if (submission.type === 'Pokemon') {
        await pokemonAPI.updateStatus(submission.submissionId, action === 'approve' ? 'approved' : 'rejected');
      } else {
        await itemsAPI.updateStatus(submission.submissionId, action === 'approve' ? 'approved' : 'rejected');
      }

      // Refresh the submissions list
      await fetchSubmissions();

      // Close the modal
      setSelectedSubmission(null);
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${action} submission`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSubmission = (submission: CombinedSubmission) => {
    setIsEditing(true);
    setEditData({ ...submission });
  };

  const handleSaveEdit = async () => {
    if (!editData) return;

    setActionLoading(true);

    try {
      if (editData.type === 'Pokemon') {
        // Update Pokemon submission
        await pokemonAPI.update(editData.submissionId, {
          pokemon_name: editData.name,
          nature: editData.nature,
          ability: editData.ability,
          level: editData.level,
          price: editData.price,
          iv_hp: editData.hp_iv,
          iv_attack: editData.atk_iv,
          iv_defense: editData.def_iv,
          iv_sp_attack: editData.spatk_iv,
          iv_sp_defense: editData.spdef_iv,
          iv_speed: editData.speed_iv,
          is_shiny: editData.shiny,
          held_item: editData.form
        });
      } else {
        // Update Item submission
        await itemsAPI.update(editData.submissionId, {
          item_name: editData.name,
          price: editData.price
        });
      }

      // Refresh submissions and close edit mode
      await fetchSubmissions();
      setIsEditing(false);
      setEditData(null);
      setSelectedSubmission(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update submission');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditData(null);
  };

  const updateEditData = (updates: Partial<EditData>) => {
    setEditData((prev: EditData | null) => prev ? {...prev, ...updates} : null);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat().format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return '#4caf50';
      case 'rejected': return '#f44336';
      case 'pending': return '#ff9800';
      default: return '#666';
    }
  };

  if (!user || (user.role !== 'checker' && user.role !== 'admin')) {
    return (
      <div className="review-submissions-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="review-submissions-container">
      <header className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>🔍 Review Submissions</h1>
            <p>Approve or reject data submissions</p>
          </div>
        </div>
      </header>

      <main className="review-main">
        {/* Tab Navigation */}
        <div className="filter-buttons">
          <button
            onClick={() => setActiveTab('pending')}
            className={`review-submissions-filter-button ${activeTab === 'pending' ? 'active' : ''}`}
          >
            Pending
            <span className="count">{reviewablePendingCount}</span>
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`review-submissions-filter-button ${activeTab === 'all' ? 'active' : ''}`}
          >
            All
            <span className="count">{reviewableTotalCount}</span>
          </button>
        </div>

        {/* Filters for "All" tab only */}
        {activeTab === 'all' && (
          <div className="filters-section">
            <h3>🔍 Filters</h3>

            <div className="filters-grid">
              {/* Status Filter */}
              <div className="filter-group">
                <label>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({...prev, status: e.target.value as any}))}
                  className="filter-select"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Pokemon/Item Name */}
              <div className="filter-group">
                <label>Pokemon/Item Name</label>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={filters.pokemon}
                  onChange={(e) => setFilters(prev => ({...prev, pokemon: e.target.value}))}
                  className="filter-input"
                />
              </div>

              {/* Price Range */}
              <div className="filter-group">
                <label>Price Range (PD)</label>
                <div className="price-range">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.priceMin}
                    onChange={(e) => setFilters(prev => ({...prev, priceMin: e.target.value}))}
                    className="filter-input small"
                  />
                  <span>to</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.priceMax}
                    onChange={(e) => setFilters(prev => ({...prev, priceMax: e.target.value}))}
                    className="filter-input small"
                  />
                </div>
              </div>

              {/* Submitted By */}
              <div className="filter-group">
                <label>Submitted By</label>
                <input
                  type="text"
                  placeholder="Username..."
                  value={filters.submittedBy}
                  onChange={(e) => setFilters(prev => ({...prev, submittedBy: e.target.value}))}
                  className="filter-input"
                />
              </div>

              {/* Nature (Pokemon only) */}
              <div className="filter-group">
                <label>Nature (Pokemon only)</label>
                <input
                  type="text"
                  placeholder="Nature..."
                  value={filters.nature}
                  onChange={(e) => setFilters(prev => ({...prev, nature: e.target.value}))}
                  className="filter-input"
                />
              </div>

              {/* Clear Filters Button */}
              <div className="filter-group">
                <button
                  onClick={() => setFilters({
                    status: 'all',
                    pokemon: '',
                    priceMin: '',
                    priceMax: '',
                    soldDateFrom: '',
                    soldDateTo: '',
                    submittedFrom: '',
                    submittedTo: '',
                    submittedBy: '',
                    nature: ''
                  })}
                  className="clear-filters-button"
                >
                  🗑️ Clear All Filters
                </button>
              </div>
            </div>

            {/* Results Summary */}
            <div className="results-summary">
              <span>Showing {Math.min(startIndex + 1, totalItems)} to {Math.min(endIndex, totalItems)} of {totalItems} submissions</span>
              <small className="exclude-own-note">
                {activeTab === 'pending' as 'pending' | 'all'
                  ? '(Your own submissions are excluded from review)'
                  : '(Your own pending submissions are excluded from review)'}
              </small>
            </div>
          </div>
        )}

        {loading ? (
          <div className="review-submissions-loading">
            <div className="review-submissions-loading-spinner"></div>
            <p>Loading submissions...</p>
          </div>
        ) : (
          <>
            <div key={`grid-${activeTab}-${filters.status}`} className="submissions-grid">
              {filteredSubmissions.length === 0 ? (
                <div className="no-submissions">
                  <h3>No submissions found</h3>
                  <p>No submissions match your current filters. Try adjusting the filter criteria.</p>
                </div>
              ) : (
                paginatedSubmissions.map(submission => (
                <div key={`${filters.status}-${submission.submissionId}-${submission.status}`} className="submission-card">
                  <div className="submission-header">
                    <div className="submission-type">
                      {submission.type === 'Pokemon' ? '🔴' : '🎒'} {submission.type}
                    </div>
                    <div
                      className="submission-status"
                      style={{ backgroundColor: getStatusColor(submission.status) }}
                    >
                      {submission.status}
                    </div>
                  </div>

                  <div className="submission-content">
                    <h3>{submission.name}</h3>
                    <div className="submission-details">
                      <div className="detail-item">
                        <span className="label">Price:</span>
                        <span className="value">{formatPrice(submission.price)} PD</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Date Sold:</span>
                        <span className="value">{formatDate(submission.date_sold)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Submitted by:</span>
                        <span className="value">{submission.submitted_by_username}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">Submitted at:</span>
                        <span className="value">{formatDate(submission.submittedAt)}</span>
                      </div>
                    </div>

                    {submission.proof_url && (
                      <div className="review-submissions-proof-link">
                        <a href={submission.proof_url} target="_blank" rel="noopener noreferrer">
                          📷 View Proof
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="submission-actions">
                    {submission.status === 'pending' ? (
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="review-button"
                      >
                        Review
                      </button>
                    ) : (
                      <button
                        onClick={() => setSelectedSubmission(submission)}
                        className="view-details-button"
                      >
                        👁️ View Details
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination Controls */}
          {totalItems > itemsPerPage && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {Math.min(startIndex + 1, totalItems)} to {Math.min(endIndex, totalItems)} of {totalItems} entries
              </div>
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  First
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                <span className="pagination-current">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="pagination-btn"
                >
                  Last
                </button>
              </div>
              <div className="items-per-page-control">
                <label>
                  Items per page:&nbsp;
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page
                    }}
                    className="items-per-page-select"
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </label>
              </div>
            </div>
          )}
          </>
        )}
      </main>

      {selectedSubmission && (
        <div className="review-modal-overlay" onClick={() => setSelectedSubmission(null)}>
          <div className="review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Review Submission</h2>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="close-button"
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              {isEditing ? (
                // Edit Mode
                <div className="edit-form">
                  <h3>Edit Submission</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Name:</label>
                      <input
                        type="text"
                        value={editData?.name || ''}
                        onChange={(e) => updateEditData({ name: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Price (PD):</label>
                      <input
                        type="number"
                        value={editData?.price || ''}
                        onChange={(e) => updateEditData({ price: parseFloat(e.target.value) })}
                      />
                    </div>

                    {/* Pokemon-specific edit fields */}
                    {editData?.type === 'Pokemon' && (
                      <>
                        <div className="form-group">
                          <label>Nature:</label>
                          <input
                            type="text"
                            value={editData?.nature || ''}
                            onChange={(e) => updateEditData({ nature: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Ability:</label>
                          <input
                            type="text"
                            value={editData?.ability || ''}
                            onChange={(e) => updateEditData({ ability: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Level:</label>
                          <input
                            type="number"
                            value={editData?.level || ''}
                            onChange={(e) => updateEditData({ level: parseInt(e.target.value) })}
                          />
                        </div>

                        {/* IV Fields */}
                        <div className="iv-edit-section">
                          <h4>Individual Values (IVs)</h4>
                          <div className="iv-edit-grid">
                            <div className="form-group">
                              <label>HP:</label>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                value={editData?.hp_iv || ''}
                                onChange={(e) => updateEditData({ hp_iv: parseInt(e.target.value) })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Attack:</label>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                value={editData?.atk_iv || ''}
                                onChange={(e) => updateEditData({ atk_iv: parseInt(e.target.value) })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Defense:</label>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                value={editData?.def_iv || ''}
                                onChange={(e) => updateEditData({ def_iv: parseInt(e.target.value) })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Sp. Attack:</label>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                value={editData?.spatk_iv || ''}
                                onChange={(e) => updateEditData({ spatk_iv: parseInt(e.target.value) })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Sp. Defense:</label>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                value={editData?.spdef_iv || ''}
                                onChange={(e) => updateEditData({ spdef_iv: parseInt(e.target.value) })}
                              />
                            </div>
                            <div className="form-group">
                              <label>Speed:</label>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                value={editData?.speed_iv || ''}
                                onChange={(e) => updateEditData({ speed_iv: parseInt(e.target.value) })}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label>
                            <input
                              type="checkbox"
                              checked={editData?.shiny || false}
                              onChange={(e) => updateEditData({ shiny: e.target.checked })}
                            />
                            Shiny
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                // Display Mode
                <div className="submission-details">
                  <h3>{selectedSubmission.name}</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Price:</span>
                      <span className="value">{formatPrice(selectedSubmission.price)} PD</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Date Sold:</span>
                      <span className="value">{formatDate(selectedSubmission.date_sold)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Submitted by:</span>
                      <span className="value">{selectedSubmission.submitted_by_username}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Submitted at:</span>
                      <span className="value">{formatDate(selectedSubmission.submittedAt)}</span>
                    </div>
                    {selectedSubmission.proof_url && (
                      <div className="detail-item">
                        <span className="label">Proof:</span>
                        <span className="value">
                          <a href={selectedSubmission.proof_url} target="_blank" rel="noopener noreferrer">📷 View Proof</a>
                        </span>
                      </div>
                    )}

                    {/* Pokemon-specific information integrated into the same grid */}
                    {selectedSubmission.type === 'Pokemon' && 'nature' in selectedSubmission && (
                      <>
                        {selectedSubmission.nature && (
                          <div className="detail-item">
                            <span className="label">Nature:</span>
                            <span className="value">{selectedSubmission.nature}</span>
                          </div>
                        )}
                        {selectedSubmission.ability && (
                          <div className="detail-item">
                            <span className="label">Ability:</span>
                            <span className="value">{selectedSubmission.ability}</span>
                          </div>
                        )}
                        <div className="detail-item">
                          <span className="label">Hidden Power:</span>
                          <span className="value">
                            {(() => {
                              const ivs = {
                                hp: selectedSubmission.hp_iv,
                                attack: selectedSubmission.atk_iv,
                                defense: selectedSubmission.def_iv,
                                spAttack: selectedSubmission.spatk_iv,
                                spDefense: selectedSubmission.spdef_iv,
                                speed: selectedSubmission.speed_iv,
                              };
                              const hiddenPower = calculateHiddenPower(ivs);
                              return hiddenPower ? formatHiddenPower(hiddenPower) : 'N/A';
                            })()}
                        </span>
                      </div>
                      {selectedSubmission.form && (
                        <div className="detail-item">
                          <span className="label">Form:</span>
                          <span className="value">{selectedSubmission.form}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Individual Values section for Pokemon */}
                {selectedSubmission.type === 'Pokemon' && 'nature' in selectedSubmission && (
                  <div className="pokemon-ivs">
                    <h5>Individual Values (IVs)</h5>
                    <div className="ivs-grid">
                      <div className="iv-item">
                        <span className="iv-name">Attack:</span>
                        <span className="iv-value">{selectedSubmission.atk_iv ?? 'N/A'}</span>
                      </div>
                      <div className="iv-item">
                        <span className="iv-name">Defense:</span>
                        <span className="iv-value">{selectedSubmission.def_iv ?? 'N/A'}</span>
                      </div>
                      <div className="iv-item">
                        <span className="iv-name">Speed:</span>
                        <span className="iv-value">{selectedSubmission.speed_iv ?? 'N/A'}</span>
                      </div>
                      <div className="iv-item">
                        <span className="iv-name">Sp. Attack:</span>
                        <span className="iv-value">{selectedSubmission.spatk_iv ?? 'N/A'}</span>
                      </div>
                      <div className="iv-item">
                        <span className="iv-name">Sp. Defense:</span>
                        <span className="iv-value">{selectedSubmission.spdef_iv ?? 'N/A'}</span>
                      </div>
                      <div className="iv-item">
                        <span className="iv-name">HP:</span>
                        <span className="iv-value">{selectedSubmission.hp_iv ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>

            <div className="modal-actions">
              {isEditing ? (
                // Edit mode buttons
                <>
                  <button
                    onClick={handleSaveEdit}
                    disabled={actionLoading}
                    className="approve-button"
                  >
                    {actionLoading ? 'Saving...' : '💾 Save Changes'}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="cancel-button"
                  >
                    Cancel Edit
                  </button>
                </>
              ) : selectedSubmission.status === 'pending' ? (
                // Review mode buttons (for pending submissions)
                <>
                  <button
                    onClick={() => handleEditSubmission(selectedSubmission)}
                    className="edit-button"
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => handleReviewSubmission(selectedSubmission, 'approve')}
                    disabled={actionLoading}
                    className="approve-button"
                  >
                    {actionLoading ? 'Processing...' : '✅ Approve'}
                  </button>
                  <button
                    onClick={() => handleReviewSubmission(selectedSubmission, 'reject')}
                    disabled={actionLoading}
                    className="reject-button"
                  >
                    {actionLoading ? 'Processing...' : '❌ Reject'}
                  </button>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                // View-only mode (for approved/rejected submissions)
                <>
                  <div className="submission-status-info">
                    <span className={`status-badge ${selectedSubmission.status}`}>
                      {selectedSubmission.status === 'approved' ? '✅ Approved' : '❌ Rejected'}
                    </span>
                    <span className="status-note">This submission has already been reviewed and cannot be modified.</span>
                  </div>
                  <button
                    onClick={() => setSelectedSubmission(null)}
                    className="cancel-button"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewSubmissions;
